import {
  Box, Chip, Divider, Grid, IconButton, Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  PrimaryMultiSelect, PrimarySelect, SubTitle,
} from 'components';
import { IPlugin } from 'pages/dashboard/components/ent-account/components/edit-account/components/plugins/interface';
import { selectPluginData } from 'pages/dashboard/components/ent-account/components/edit-account/selector';
import React from 'react';
import {
  Controller, useFieldArray, useFormContext,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import { IOption } from 'interfaces';
import { ICreateSessionForm } from '../../../../interface';

interface ISessionPluginsProps {
  plugins: IPlugin[];
}

interface ICombination {
  role: string;
  activities: string[];
}

interface ITappsPluginCombinationsProps {
  pluginKey: string;
}

const TappsPluginCombinations: React.FC<ITappsPluginCombinationsProps> = ({ pluginKey }) => {
  const { control } = useFormContext<ICreateSessionForm>();
  const pluginData = useSelector(selectPluginData);

  const fieldArrayName = `metaData.${pluginKey}.combinations` as any;

  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  const roleOptions = React.useMemo((): IOption[] => {
    const data = pluginData?.[pluginKey]?.roles;
    if (Array.isArray(data)) {
      return data.map((item: any) => ({
        id: String(item.id),
        value: String(item.id),
        text: item.name,
      }));
    }
    return [];
  }, [pluginData, pluginKey]);

  const activityOptions = React.useMemo((): IOption[] => {
    const data = pluginData?.[pluginKey]?.activities;
    if (Array.isArray(data)) {
      return data.map((item: any) => ({
        id: String(item.id),
        value: String(item.id),
        text: item.name,
      }));
    }
    return [];
  }, [pluginData, pluginKey]);

  const handleAdd = () => {
    append({ role: '', activities: [] } as ICombination);
  };

  return (
    <div>
      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Grid item>
          <Typography variant="body1">Role &amp; Activity Combinations</Typography>
        </Grid>
        <Grid item>
          <IconButton
            color="primary"
            onClick={handleAdd}
            size="small"
            title="Add combination"
          >
            <AddCircleOutlineIcon />
            <Typography variant="body2" sx={{ ml: 0.5 }}>
              Add
            </Typography>
          </IconButton>
        </Grid>
      </Grid>

      {fields.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, fontStyle: 'italic' }}
        >
          No combinations added. Click &quot;Add&quot; to create a role and activity combination.
        </Typography>
      )}

      {fields.map((field, index) => (
        <Box
          key={field.id}
          sx={{
            border: '1px solid #E5E5E5',
            borderRadius: '5px',
            p: 2,
            mb: 2,
            background: '#FAFAFA',
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Grid item>
              <Typography variant="body2" color="text.secondary">
                Combination #
                {index + 1}
              </Typography>
            </Grid>
            <Grid item>
              <IconButton
                size="small"
                color="error"
                onClick={() => remove(index)}
                title="Remove combination"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <div className="form-item">
            <Controller
              control={control}
              name={`${fieldArrayName}.${index}.role` as any}
              render={({ field: f }) => (
                <PrimarySelect
                  {...f}
                  label="Role"
                  placeholder="Select a role"
                  options={roleOptions}
                  value={f.value ?? ''}
                  onChange={(e: any) => f.onChange(e.target.value)}
                />
              )}
            />
          </div>

          <div className="form-item">
            <Controller
              control={control}
              name={`${fieldArrayName}.${index}.activities` as any}
              render={({ field: f }) => (
                <PrimaryMultiSelect
                  options={activityOptions}
                  value={Array.isArray(f.value) ? f.value : []}
                  onChange={(e: any) => f.onChange(e.target.value)}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value: any) => (
                        <Chip
                          key={value}
                          label={activityOptions.find((opt) => opt.value === value)?.text || value}
                          color="primary"
                        />
                      ))}
                    </Box>
                  )}
                />
              )}
            />
          </div>
        </Box>
      ))}
    </div>
  );
};

const SessionPlugins: React.FC<ISessionPluginsProps> = ({ plugins }) => (
  <div className="form-item">
    <SubTitle>Plugins</SubTitle>

    {plugins.map((plugin: IPlugin) => (
      <div key={plugin.id}>
        <Typography sx={{ mb: 1 }}>{plugin.name}</Typography>
        {plugin.pluginConfig?.map((configItem) => {
          if (configItem.type === 'combinations') {
            return (
              <TappsPluginCombinations
                key={configItem.key}
                pluginKey={plugin.key}
              />
            );
          }
          return null;
        })}
      </div>
    ))}
  </div>
);

export default SessionPlugins;
